import { StudentApplication } from '../components/Application/types'
import { useMemo } from 'react'
import { ApplicationState, Student, useGetApplicationPageProps, useGetStudent } from '.'
import { z } from 'zod'
import {
  DocumentTags,
  EducationLevel,
  LanguageExamAvailability,
  RelationshipType,
  TimelineTabs,
  ApplicationSteps,
} from '../utils/enums'
import { specificFieldValidations } from '../utils/specificFieldValidations'
import { hasNewLanguageProficiencySchema } from '../components/Application/ApplicationForms/utils'

export type TimelineObject = {
  key: TimelineTabs
  label: string
  step: number
  cardNumber: number
  icon: string
  title: string
  formId: string
  completed: boolean
}

const timelineItems: TimelineObject[] = [
  {
    key: TimelineTabs.PERSONAL_INFORMATION,
    label: 'Personal Information',
    step: ApplicationSteps.PERSONAL_INFORMATION,
    cardNumber: 1,
    icon: '👤',
    title: 'Personal Information',
    formId: crypto.randomUUID(),
    completed: false,
  },
  {
    key: TimelineTabs.CONTACT_INFORMATION,
    label: 'Contact Information',
    step: ApplicationSteps.CONTACT_INFORMATION,
    cardNumber: 2,
    icon: '☎️',
    title: 'Contact Information',
    formId: crypto.randomUUID(),
    completed: false,
  },
  {
    key: TimelineTabs.EDUCATION_HISTORY,
    label: 'Education History',
    step: ApplicationSteps.EDUCATION_HISTORY,
    cardNumber: 3,
    icon: '📚',
    title: 'Education History',
    formId: crypto.randomUUID(),
    completed: false,
  },
  {
    key: TimelineTabs.LANGUAGE_PROFICIENCY,
    label: 'Language Proficiency',
    step: ApplicationSteps.LANGUAGE_PROFICIENCY,
    cardNumber: 4,
    icon: '🗣️',
    title: 'Language Proficiency',
    formId: crypto.randomUUID(),
    completed: false,
  },
  {
    key: TimelineTabs.OTHER_REQUIRED_DOCUMENTS,
    label: 'Other Required Documents',
    step: ApplicationSteps.OTHER_REQUIRED_DOCUMENTS,
    cardNumber: 5,
    icon: '📄',
    title: 'Other Required Documents',
    formId: crypto.randomUUID(),
    completed: false,
  },
]

export function useGetTimelineItems(
  application: StudentApplication | null, 
  selectedTab?: number
): { 
  timelineItems: TimelineObject[], 
  lastTimeline: number,
  cardNumber: number
} {
  const { applicationPageProps } = useGetApplicationPageProps()
  const { student } = useGetStudent({ id: application?.relationships?.student?.data.id })
  const { 
    isSpecificFieldRequired, 
    getRequiredLanguageProficiencyProofTypes, 
    getOtherRequiredDocumentTypes,
  } = specificFieldValidations(
    applicationPageProps?.specificFields,
  )

  return useMemo(() => {
    const items = !application 
      ? timelineItems 
      : timelineItems.map((timelineItem: TimelineObject) => ({
          ...timelineItem,
          completed: isTabCompleted(timelineItem, application, student, {
            isSpecificFieldRequired,
            getRequiredLanguageProficiencyProofTypes,
            getOtherRequiredDocumentTypes,
          }),
        }));

    const effectiveTab = selectedTab && !items.find(item => item.key === selectedTab)
      ? items.at(-1)?.key
      : (selectedTab ?? 1);

    return {
      timelineItems: items,
      lastTimeline: items.length,
      cardNumber: items.find(item => item.key === effectiveTab)?.cardNumber ?? 1
    };
  }, [application, selectedTab, student, applicationPageProps]);
}

type TabCompletedOptions = {
  isSpecificFieldRequired: (fieldPath: string) => boolean
  getRequiredLanguageProficiencyProofTypes: () => DocumentTags[]
  getOtherRequiredDocumentTypes: () => DocumentTags[]
}

export function isTabCompleted(
  timeLineItem: TimelineObject, 
  application: StudentApplication,
  student: Student | undefined,
  {
    isSpecificFieldRequired,
    getRequiredLanguageProficiencyProofTypes,
    getOtherRequiredDocumentTypes,
  }: TabCompletedOptions,
) {
  if (!application) {
    return false
  }

  if (application.attributes?.applicationState !== ApplicationState.DRAFT) {
    return true
  }

  switch (timeLineItem.key) {
    case TimelineTabs.PERSONAL_INFORMATION: {
      return validatesPersonalInformation(application, student, isSpecificFieldRequired)
    }

    case TimelineTabs.CONTACT_INFORMATION: {
      return validatesContactInformation(application)
    }

    case TimelineTabs.EDUCATION_HISTORY: {
      return validatesEducationHistory(application, student, isSpecificFieldRequired)
    }

    case TimelineTabs.LANGUAGE_PROFICIENCY: {
      return validatesLanguageProficiency(application, getRequiredLanguageProficiencyProofTypes)
    }

    case TimelineTabs.OTHER_REQUIRED_DOCUMENTS: {
      return validatesOtherRequiredDocuments(application, getOtherRequiredDocumentTypes)
    }
  }
}

const required = () => z.any().refine(val => val !== undefined && val !== null, "Field is required");

function validatesPersonalInformation(
  application: StudentApplication,
  student: Student | undefined,
  isSpecificFieldRequired: (fieldPath: string) => boolean,
) {
  const personalInformationSchema = z.object({
    basicPersonalInformation: z.object({
      givenName: z.string().min(1),
      gender: z.string(),
      dateOfBirth: z.coerce.date().max(new Date()),
      nationality: z.string(),
    }),
    addresses: z.object({
      residentialAddress: z.object({
        country: z.string(),
        addressLine1: z.string().min(1),
        province: z.string().min(1),
        city: z.string().min(1),
      }),
      mailingAddress: z.object({
        country: z.string(),
        addressLine1: z.string().min(1),
        province: z.string().min(1),
        city: z.string().min(1),
      }),
    }),
  })

  const personalInformationSpecificFieldsSchema = z.object({
    ...(isSpecificFieldRequired('personalInformation.previousName')
      ? { previousName: required() }
      : {}),
    ...(isSpecificFieldRequired('personalInformation.preferredName')
      ? { preferredName: required() }
      : {}),
    ...(isSpecificFieldRequired('personalInformation.firstLanguageSpoken')
      ? { firstLanguageSpoken: required() }
      : {}),
  })

  const statusAndCitizenshipSchema = z.object({
    statusAndCitizenship: z.object({
      passports: z
        .record(
          z.object({
            passportId: z.string().min(1),
          }),
        )
        .refine(data => Object.keys(data).length > 0),
      destinationCountryStatus: z.string(),
    }),
  })

  const destinationCountryFileSchema = z
    .record(
      z.object({
        type: z.nativeEnum(DocumentTags),
        uploadStatus: z.string().optional(),
      }),
    )
    .refine(data => {
      return Object.values(data).some(
        file => file.type === DocumentTags.DESTINATION_COUNTRY_STATUS,
      )
    })
  return (
    personalInformationSchema.safeParse(application?.attributes?.personalInformation).success &&
    personalInformationSpecificFieldsSchema.safeParse(student?.attributes?.personalInformation)
      .success &&
    statusAndCitizenshipSchema.safeParse(application?.attributes).success &&
    (application?.attributes?.statusAndCitizenship?.destinationCountryStatus === 'NO_STATUS' ||
      destinationCountryFileSchema.safeParse(application?.attributes?.files).success)
  )
}

function validatesContactInformation(
  application: StudentApplication,
) {
  const contactInformationSchema = z.object({
    contactInformation: z.object({
      phoneNumber: z.string().min(1),
    }),
    emergencyContactSchema: z.union([
      z.object({
        email: z.string().email(),
        name: z.string().min(1),
        phoneNumber: z.string().min(1),
        relationship: z.nativeEnum(RelationshipType),
      }),
      z.undefined(),
    ]),
  })

  return contactInformationSchema.safeParse(application?.attributes?.personalInformation).success
}

function validatesEducationHistory(
  application: StudentApplication,
  student: Student | undefined,
  isSpecificFieldRequired: (fieldPath: string) => boolean,
) {
  const educationHistorySchema = z.object({
    educationHistory: z.record(
      z.object({
        educationLevel: z.nativeEnum(EducationLevel),
        educationCountry: z.string(),
        schoolName: z.string().min(1),
        credentialReceived: z.string().min(1),
        startDate: z.coerce.date(),
        endDate: z.coerce.date(),
      }),
    ),
    files: z
      .record(
        z.object({
          type: z.nativeEnum(DocumentTags),
          uploadStatus: z.string().optional(),
        }),
      )
      .refine(data => {
        return Object.values(data).some(
          file =>
            file.type === DocumentTags.TRANSCRIPTS || file.type === DocumentTags.CERTIFICATE,
        )
      }),
  })

  const schoolInformationSpecificFieldsSchema = z.object({
    ...(isSpecificFieldRequired('schoolInformation.haveAttendedSchool')
      ? { haveAttendedSchool: required() }
      : {}),
    ...(isSpecificFieldRequired('schoolInformation.ontarioEducationNumber')
      ? { ontarioEducationNumber: required() }
      : {}),
    ...(isSpecificFieldRequired('schoolInformation.albertaStudentNumber')
      ? { albertaStudentNumber: required() }
      : {}),
    ...(isSpecificFieldRequired('schoolInformation.personalEducationNumber')
      ? { personalEducationNumber: required() }
      : {}),
    ...(isSpecificFieldRequired('schoolInformation.schoolAssignedStudentId')
      ? { schoolAssignedStudentId: required() }
      : {}),
  })

  return (
    educationHistorySchema.safeParse(application.attributes).success &&
    schoolInformationSpecificFieldsSchema.safeParse(student?.attributes?.schoolInformation)
      .success
  )
}

function validatesLanguageProficiency(
  application: StudentApplication,
  getRequiredLanguageProficiencyProofTypes: () => DocumentTags[],
) { 
  const defaultLanguageProficiencySchema = z.object({
    hasLanguageProficiency: z.enum([
      LanguageExamAvailability.DONT_NEED,
      LanguageExamAvailability.WILL_HAVE,
    ]),
  })

  const proofTypeOther = z.object({
    testType: z.literal('OTHER'),
    description: z.string().max(200),
  })

  const proofTypeUploadOnly = z.object({
    testType: z.enum(['CAEL', 'MET', 'CAMBRIDGE', 'EPP', 'CANTEST', 'LANGUAGECERT_ESOL', 'LEAP_4_ADVANCED']),
  })

  const proofTypeDuolingo = z.object({
    testType: z.literal('DUOLINGO'),
    certificateNumber: z.string().min(1),
    testDate: z.coerce.date(),
    overallScore: z.coerce.number(),
    integratedSubscores: z.object({
      production: z.number(),
      literacy: z.number(),
      comprehension: z.number(),
      conversation: z.number(),
    }),
    subscores: z.object({
      listening: z.number(),
      reading: z.number(),
      speaking: z.number(),
      writing: z.number(),
    }),
  })

  const haveLegacyLanguageProficiencySchema = z.object({
    hasLanguageProficiency: z.literal(LanguageExamAvailability.HAVE),
    languageProficiencyData: z.record(
      z.discriminatedUnion('testType', [
        proofTypeOther,
        proofTypeUploadOnly,
        proofTypeDuolingo,
        z.object({
          certificateNumber: z.string().min(1),
          testDate: z.coerce.date(),
          testType: z.enum(['IELTS', 'PTE', 'TOEFL']),
          overallScore: z.coerce.number(),
          subscores: z.object({
            listening: z.number(),
            reading: z.number(),
            speaking: z.number(),
            writing: z.number(),
          }),
        }),
      ]),
    ),
  })

  const legacyLanguageProficiencySchema = z.object({
    languageProficiency: z.discriminatedUnion('hasLanguageProficiency', [
      defaultLanguageProficiencySchema,
      haveLegacyLanguageProficiencySchema,
    ]),
    files: z
      .record(
        z.object({
          type: z.nativeEnum(DocumentTags),
          uploadStatus: z.string().optional(),
        }),
      )
      .refine(data => {
        const isLanguageProficiencySpecificTypeRequired = getRequiredLanguageProficiencyProofTypes()
        if (isLanguageProficiencySpecificTypeRequired?.length) {
          return isLanguageProficiencySpecificTypeRequired.every(docType => Object.values(data).some(file => file.type === docType))
        }

        if (
          application?.attributes?.languageProficiency?.hasLanguageProficiency &&
          (application?.attributes?.languageProficiency?.hasLanguageProficiency as string) !==
            LanguageExamAvailability.HAVE
        ) {
          return true
        }

        return Object.values(data).some(
          file =>
            file.type === DocumentTags.LANGUAGE_TEST_IELTS ||
            file.type === DocumentTags.LANGUAGE_TEST_PTE ||
            file.type === DocumentTags.LANGUAGE_TEST_TOEFL ||
            file.type === DocumentTags.LANGUAGE_TEST_DUOLINGO ||
            file.type === DocumentTags.LANGUAGE_TEST_CAEL ||
            file.type === DocumentTags.LANGUAGE_TEST_MET ||
            file.type === DocumentTags.LANGUAGE_TEST_EPP ||
            file.type === DocumentTags.LANGUAGE_TEST_CAMBRIDGE ||
            file.type === DocumentTags.LANGUAGE_TEST_OTHER ||
            file.type === DocumentTags.LANGUAGE_TEST_CANTEST ||
            file.type === DocumentTags.LANGUAGE_TEST_LANGUAGECERT_ESOL ||
            file.type === DocumentTags.LANGUAGE_TEST_LEAP_4_ADVANCED,
        )
      }),
  })
  const haveLanguageProficiencySchema = z.object({
    hasLanguageProficiency: z.literal(LanguageExamAvailability.HAVE),
    languageProficiencyData: z.record(
      z.object({
        languageProficiencyId: z.string().min(1),
      })
    ),
  })

  const languageProficiencySchema = z.object({
    languageProficiency: z.discriminatedUnion('hasLanguageProficiency', [
      defaultLanguageProficiencySchema,
      haveLanguageProficiencySchema,
    ])})

  const { hasNewLanguageProficiencyRecord } = hasNewLanguageProficiencySchema(application);

  return hasNewLanguageProficiencyRecord ? languageProficiencySchema.safeParse(application.attributes).success : legacyLanguageProficiencySchema.safeParse(application.attributes).success
}

function validatesOtherRequiredDocuments(
  application: StudentApplication,
  getOtherRequiredDocumentTypes: () => DocumentTags[],
) {
  const additionalDocumentsFileSchema = z.object({
    files: z
      .record(
        z.object({
          type: z.nativeEnum(DocumentTags),
          uploadStatus: z.string().optional(),
        }),
      )
      .refine(data => {
        const isOtherRequiredDocumentSpecificTypeRequired = getOtherRequiredDocumentTypes()
        if (isOtherRequiredDocumentSpecificTypeRequired?.length) {
          return isOtherRequiredDocumentSpecificTypeRequired.every(docType => Object.values(data).some(file => file.type === docType))
        }

        return Object.values(data).some(
          file =>
            file.type === DocumentTags.ADDITIONAL_DOCUMENT ||
            file.type === DocumentTags.STATEMENT_OF_PURPOSE ||
            file.type === DocumentTags.LETTER_OF_RECOMMENDATION ||
            file.type === DocumentTags.CONSENT_FORM ||
            file.type === DocumentTags.GMAT_SCORE ||
            file.type === DocumentTags.GRE_SCORE ||
            file.type === DocumentTags.RESUME ||
            file.type === DocumentTags.EMPLOYMENT_LETTER ||
            file.type === DocumentTags.VOLUNTEER_LETTER ||
            file.type === DocumentTags.BIRTH_CERTIFICATE ||
            file.type === DocumentTags.WES_EVALUATION_REPORT ||
            file.type === DocumentTags.PORTFOLIO ||
            file.type === DocumentTags.FINANCIAL_PROOF ||
            file.type === DocumentTags.IQAS_ASSESSMENT ||
            file.type === DocumentTags.CASPER_TEST_SCORE_REPORT ||
            file.type === DocumentTags.SNAPSHOT ||
            file.type === DocumentTags.PROOF_OF_NAME_CHANGE ||
            file.type === DocumentTags.RELEASE_OF_INFORMATION_FORM ||
            file.type === DocumentTags.AUTHORIZED_RP_RELEASE_OF_INFORMATION_FORM,
        )
      }),
  })

  return additionalDocumentsFileSchema.safeParse(application.attributes).success
}
